import React from 'react';
import { Button } from '@/shared/ui/button';
import { Plus } from 'lucide-react';
import { GenericFormDialog, FormField } from './GenericFormDialog';

/**
 * 汎用エンティティ追加ボタンのプロパティ
 */
export interface AddEntityButtonProps<T> {
  entityName: string;
  fields: FormField[];
  onSubmit: (data: Partial<Record<string, any>>) => Promise<any>;
  isLoading?: boolean;
  buttonText?: string;
  buttonVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

/**
 * 汎用エンティティ追加ボタンコンポーネント
 * マスターデータの追加ボタンとして再利用可能な汎用ボタンコンポーネントを提供する
 * 
 * @param entityName - エンティティ名
 * @param fields - フォームフィールド定義
 * @param onSubmit - 送信時のコールバック関数
 * @param isLoading - ローディング状態
 * @param buttonText - ボタンテキスト
 * @param buttonVariant - ボタンバリアント
 * @param buttonSize - ボタンサイズ
 * @param className - 追加のCSSクラス名
 * @returns 汎用エンティティ追加ボタンコンポーネント
 */
export const AddEntityButton = React.memo(<T extends Record<string, any>>({
  entityName,
  fields,
  onSubmit,
  isLoading = false,
  buttonText,
  buttonVariant = 'default',
  buttonSize = 'default',
  className = '',
}: AddEntityButtonProps<T>) => {
  const defaultButtonText = buttonText || `${entityName}を追加`;
  const dialogTitle = `新しい${entityName}を追加`;

  const triggerButton = (
    <Button
      variant={buttonVariant}
      size={buttonSize}
      className={className}
      disabled={isLoading}
    >
      <Plus className="h-4 w-4 mr-2" />
      {defaultButtonText}
    </Button>
  );

  return (
    <GenericFormDialog
      trigger={triggerButton}
      title={dialogTitle}
      fields={fields}
      onSubmit={onSubmit}
      isLoading={isLoading}
      submitLabel="追加"
    />
  );
});

AddEntityButton.displayName = 'AddEntityButton';